import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { divisionsAPI, teamsAPI, groundsAPI, conditionsAPI, matrixAPI, seriesAPI } from '../../services/api';
import { Spinner } from '../../components/ui';
import { SingleDatePicker } from '../../components/DateRangePicker';
import toast from 'react-hot-toast';

// ─── helpers ─────────────────────────────────────────────────────────────────
const fmt12 = t => {
  if (!t) return '—';
  const [hStr, mStr] = String(t).split(':');
  const h = parseInt(hStr, 10);
  if (isNaN(h)) return t;
  return `${h > 12 ? h-12 : h === 0 ? 12 : h}:${mStr||'00'} ${h >= 12 ? 'PM' : 'AM'}`;
};

// ─── Collapsible section ──────────────────────────────────────────────────────
function Section({ icon, title, badge, badgeColor='var(--accent)', defaultOpen=true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card" style={{ marginBottom:12 }}>
      <div onClick={() => setOpen(o=>!o)}
        style={{ display:'flex', alignItems:'center', gap:8, padding:'12px 16px', cursor:'pointer', userSelect:'none' }}>
        <span style={{ fontSize:15 }}>{icon}</span>
        <span style={{ fontSize:12, fontWeight:800, color:'var(--text)', textTransform:'uppercase', letterSpacing:'.5px', flex:1 }}>{title}</span>
        {badge !== undefined && (
          <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:10,
            background:`color-mix(in srgb, ${badgeColor} 15%, transparent)`,
            color:badgeColor, border:`1px solid color-mix(in srgb, ${badgeColor} 30%, transparent)` }}>{badge}</span>
        )}
        <span style={{ fontSize:10, color:'var(--dim)' }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && <div style={{ padding:'0 16px 14px' }}>{children}</div>}
    </div>
  );
}

// ─── Check row ────────────────────────────────────────────────────────────────
function CheckRow({ status, label, value, sub, href }) {
  const icons  = { ok:'✅', warn:'⚠️', error:'❌', info:'ℹ️' };
  const colors = { ok:'var(--text)', warn:'var(--gold)', error:'var(--red)', info:'var(--blue)' };
  return (
    <div style={{ display:'flex', gap:10, padding:'7px 0', borderBottom:'1px solid var(--border)' }}>
      <span style={{ fontSize:12, flexShrink:0 }}>{icons[status]||'·'}</span>
      <div style={{ flex:1 }}>
        <div style={{ fontSize:10, color:'var(--dim)', fontWeight:600, textTransform:'uppercase', letterSpacing:'.3px' }}>{label}</div>
        <div style={{ fontSize:12, fontWeight:600, color:colors[status]||'var(--text)', marginTop:1 }}>
          {value}
          {href && <a href={href} style={{ fontSize:10, color:'var(--blue)', marginLeft:8, textDecoration:'none', fontWeight:400 }}>→ fix</a>}
        </div>
        {sub && <div style={{ fontSize:10, color:'var(--dim)', marginTop:2, fontStyle:'italic' }}>{sub}</div>}
      </div>
    </div>
  );
}

// ─── Log entry ────────────────────────────────────────────────────────────────
function LogEntry({ entry }) {
  const colors = { ok:'var(--accent)', warn:'var(--gold)', error:'var(--red)', info:'var(--mid)' };
  const icons  = { ok:'✓', warn:'⚠', error:'✕', info:'·' };
  return (
    <div style={{ display:'flex', gap:8, padding:'3px 0', borderBottom:'1px solid var(--border)', fontSize:11.5 }}>
      <span style={{ color:colors[entry.type]||'var(--mid)', fontWeight:700, flexShrink:0 }}>{icons[entry.type]||'·'}</span>
      <span style={{ color:entry.type==='error'?'var(--red)':entry.type==='warn'?'var(--gold)':'var(--mid)', lineHeight:1.5 }}>{entry.msg}</span>
    </div>
  );
}

// ─── Ground Slot Card ─────────────────────────────────────────────────────────
function GroundCard({ g, filtered, filterType, dimmed }) {
  // filterType: 'ground' | 'month' | null
  const isPremier = g.type === 'premier';
  const left = g.totalSlots - g.takenByPremier - g.takenByDiv12 - g.takenByDiv3;
  const used  = g.takenByPremier + g.takenByDiv12 + g.takenByDiv3;
  const pct   = g.totalSlots > 0 ? Math.round((used / g.totalSlots) * 100) : 0;
  const isFull = left <= 0;
  const isLow  = left > 0 && left <= 3;

  const accentColor = isPremier ? '#F5A623' : '#4A90D9';
  const dimAlpha = dimmed ? 0.3 : 1;

  return (
    <div style={{
      background: 'var(--card)',
      border: `1px solid ${filtered ? accentColor : 'var(--border)'}`,
      borderRadius: 10,
      padding: '12px 14px',
      opacity: dimAlpha,
      transition: 'opacity .2s, border-color .2s',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Top stripe */}
      <div style={{ position:'absolute', top:0, left:0, right:0, height:3,
        background: isPremier
          ? 'linear-gradient(90deg, #F5A623, #FF6B35)'
          : 'linear-gradient(90deg, #4A90D9, #7B68EE)',
      }} />

      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:8, marginTop:4 }}>
        <div>
          <div style={{ fontSize:12, fontWeight:800, color:'var(--text)', lineHeight:1.2 }}>{g.name}</div>
          <span style={{ fontSize:9, fontWeight:700, padding:'1px 6px', borderRadius:8, marginTop:3, display:'inline-block',
            background: isPremier ? 'rgba(245,166,35,.15)' : 'rgba(74,144,217,.15)',
            color: isPremier ? '#F5A623' : '#4A90D9' }}>
            {isPremier ? '⭐ PREMIER' : '📍 STANDARD'}
          </span>
        </div>
        <div style={{ textAlign:'right' }}>
          <div style={{ fontSize:18, fontWeight:900, color: isFull ? 'var(--red)' : isLow ? 'var(--gold)' : 'var(--accent)',
            fontFamily:"'Roboto Mono',monospace", lineHeight:1 }}>{left}</div>
          <div style={{ fontSize:9, color:'var(--dim)', fontWeight:600 }}>
            {filterType === 'month' ? 'UNUSED THIS MONTH' : 'LEFT FOR DIV3'}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ height:5, borderRadius:3, background:'var(--border)', marginBottom:8, overflow:'hidden' }}>
        <div style={{ height:'100%', borderRadius:3, transition:'width .3s',
          width:`${Math.min(pct, 100)}%`,
          background: isFull ? 'var(--red)' : isLow ? 'var(--gold)' : 'var(--accent)' }} />
      </div>

      {/* Stats row */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:4 }}>
        {[
          { label:'Total', value:g.totalSlots, color:'var(--mid)' },
          { label:'Premier', value:g.takenByPremier, color:'#F5A623' },
          { label:'Div 1/2', value:g.takenByDiv12, color:'#4A90D9' },
          { label:'Div 3', value:g.takenByDiv3||0, color:'#7B68EE' },
        ].map(s => (
          <div key={s.label} style={{ textAlign:'center', padding:'4px 2px',
              background:'var(--surface)', borderRadius:5 }}>
            <div style={{ fontSize:13, fontWeight:800, color:s.value > 0 ? s.color : 'var(--dim)',
              fontFamily:"'Roboto Mono',monospace" }}>{s.value}</div>
            <div style={{ fontSize:8, color:'var(--dim)', fontWeight:600, textTransform:'uppercase' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Availability dates sub-info */}
      <div style={{ marginTop:8, fontSize:10, color:'var(--dim)' }}>
        {g.avDates} available {g.avDates === 1 ? 'date' : 'dates'} · {g.maxPD} game{g.maxPD!==1?'s':''}/day
        {g.isExclusive && <span style={{ color:'var(--gold)', marginLeft:6 }}>· C10 exclusive</span>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SCHEDULE GENERATOR PAGE
// ═══════════════════════════════════════════════════════════════════════════════
export default function ScheduleGeneratorPage() {
  const navigate = useNavigate();

  const [seriesList,   setSeriesList]   = useState([]);
  const [selSeries,    setSelSeries]    = useState('');
  const [seriesObj,    setSeriesObj]    = useState(null);
  const [divisions,    setDivisions]    = useState([]);
  const [teams,        setTeams]        = useState([]);
  const [grounds,      setGrounds]      = useState([]);
  const [conditions,   setConditions]   = useState([]);
  const [matrixInfo,   setMatrixInfo]   = useState([]);
  const [availMap,     setAvailMap]     = useState({});
  const [groundStats,  setGroundStats]  = useState([]);
  const [allFixtures,  setAllFixtures]  = useState([]);  // raw confirmed fixtures for filter calc
  const [loadingMeta,  setLoadingMeta]  = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  // ── Control Panel state ───────────────────────────────────────────────────
  const [divOrder,       setDivOrder]       = useState(['Premier','Division 1','Division 2','Division 3']);
  const [monthlyTargets, setMonthlyTargets] = useState({
    'Premier':    { may:16, jun:18, jul:18, aug:18 },
    'Division 1': { may:24, jun:28, jul:28, aug:28 },
    'Division 2': { may:22, jun:26, jul:28, aug:32 },
    'Division 3': { may:28, jun:26, jul:24, aug:20 }
  });
  const [groundPriority, setGroundPriority] = useState([
    'Troy','Bloomer','Jayne','Lasky','Lyon Oaks','Sterling Heights','Northville',
    'Belle-Isle','Redford','Murphy','Canton','Clinton','Trombly'
  ]);
  const [seededMonth,       setSeededMonth]       = useState('2026-08');
  const [seededFloorDate,   setSeededFloorDate]   = useState('2026-08-01');
  const [seededReserveCount,setSeededReserveCount]= useState(5);
  const [poolAGamesReq,     setPoolAGamesReq]     = useState(8);
  const [conflictStrategy,  setConflictStrategy]  = useState('fill_premier_first');
  const [dragDivIdx,        setDragDivIdx]        = useState(null);
  const [dragGndIdx,        setDragGndIdx]        = useState(null);
  const [showControlPanel,  setShowControlPanel]  = useState(true);
  // Slot inventory: raw capacity per month for non-Premier divisions
  // { '2026-05': N, '2026-06': N, ... } — loaded once per series
  const [slotInventory, setSlotInventory] = useState({});

  // ── Filter state for ground slot analysis ────────────────────────────────
  const [filterGround, setFilterGround] = useState('');  // ground name
  const [filterMonth,  setFilterMonth]  = useState('');  // 'YYYY-MM'

  // ── Generate state ───────────────────────────────────────────────────────
  const [genPhase,   setGenPhase]   = useState('idle');
  const [genLog,     setGenLog]     = useState([]);
  const [genResult,  setGenResult]  = useState(null);
  const [generating, setGenerating] = useState(false);
  const logEndRef = useRef(null);
  useEffect(() => { logEndRef.current?.scrollIntoView({ behavior:'smooth' }); }, [genLog]);

  useEffect(() => {
    seriesAPI.list().then(r => {
      const list = r.data || [];
      setSeriesList(list);
      if (list.length > 0) setSelSeries(list[0].id);
    });
  }, []);

  useEffect(() => { if (selSeries) loadMeta(selSeries); }, [selSeries]);

  const loadMeta = async (sid) => {
    setLoadingMeta(true);
    try {
      const [divRes, teamRes, condRes] = await Promise.all([
        divisionsAPI.list(sid).then(r => r.data || []),
        teamsAPI.list({ series_id: sid }).then(r => r.data || []),
        conditionsAPI.list({ series_id: sid }).then(r => r.data || r || []),
      ]);
      setDivisions(divRes);
      setTeams(teamRes);
      setConditions(condRes);
      setSeriesObj(seriesList.find(s => s.id === sid) || null);

      const freshGrounds = await groundsAPI.list().then(r => r.data || r || []);
      setGrounds(freshGrounds);

      const mInfo = await Promise.all(divRes.map(async d => {
        try {
          const mat = await matrixAPI.get(d.id).then(r => r.data || []);
          return { division:d, hasMatrix:mat.length>0, matrixCount:mat.length,
            mustNotPlay: mat.filter(m=>m.restriction==='Must not play').length,
            restricted:  mat.filter(m=>m.restriction&&m.restriction!=='Must not play').length };
        } catch { return { division:d, hasMatrix:false, matrixCount:0, mustNotPlay:0, restricted:0 }; }
      }));
      setMatrixInfo(mInfo);

      const avMap = {};
      await Promise.all(freshGrounds.map(async g => {
        try {
          const av = await fetch(`/api/grounds/${g.id}/availability?series_id=${sid}`,
            { headers: { Authorization: `Bearer ${localStorage.getItem('michca_token')}` } }
          ).then(r => r.json());
          avMap[g.id] = (av.availability || av || []).filter(a => a.status==='available').length;
        } catch { avMap[g.id] = 0; }
      }));
      setAvailMap(avMap);
      await loadGroundStats(sid, condRes, freshGrounds, avMap);

      // Load raw slot inventory per month (capacity available for all divisions)
      // This drives the budget bar in the monthly targets section
      try {
        const grRules = condRes.filter(c => c.condition_type === 'ground_rule');
        const grBkgs  = condRes.filter(c => c.condition_type === 'ground_booking');
        const exclGids = grBkgs.map(b => b.value?.ground_id);
        const inv = {};
        await Promise.all(freshGrounds.map(async g => {
          if (exclGids.includes(g.id)) return; // skip C10 exclusive grounds
          try {
            const avRes = await fetch(`/api/grounds/${g.id}/availability?series_id=${sid}`,
              { headers: { Authorization: `Bearer ${localStorage.getItem('michca_token')}` } }
            ).then(r => r.json());
            const avDates = (avRes.availability || avRes || []).filter(a => a.status==='available')
              .map(a => String(a.available_date).split('T')[0]);
            const gr = grRules.find(r => r.value?.ground_id === g.id);
            const maxPD = gr?.value?.max_games_per_day ? parseInt(gr.value.max_games_per_day) : 1;
            avDates.forEach(d => {
              const m = d.substring(0,7);
              inv[m] = (inv[m]||0) + maxPD;
            });
          } catch { /* non-fatal */ }
        }));
        setSlotInventory(inv);
      } catch { /* non-fatal */ }
    } catch { toast.error('Failed to load data'); }
    finally { setLoadingMeta(false); }
  };

  const loadGroundStats = async (sid, conds, groundList, avMap) => {
    setLoadingStats(true);
    try {
      const token = localStorage.getItem('michca_token');
      const res = await fetch(`/api/fixtures?series_id=${sid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const raw = await res.json();
      const confirmed = (Array.isArray(raw) ? raw : raw.data || []).filter(f => f.status === 'confirmed');
      setAllFixtures(confirmed);
      buildGroundStats(confirmed, conds, groundList, avMap);
    } catch (e) { console.error('Ground stats error', e); }
    finally { setLoadingStats(false); }
  };

  // Build stats — can be called with optional month/ground filter
  const buildGroundStats = (fixtures, conds, groundList, avMap, monthFilter, groundFilter) => {
    const gl = groundList.length ? groundList : grounds;
    const condArr = conds || conditions;

    // Filter fixtures by month/ground if provided
    const filtered = fixtures.filter(f => {
      if (monthFilter && f.match_date) {
        const m = String(f.match_date).split('T')[0].substring(0,7);
        if (m !== monthFilter) return false;
      }
      if (groundFilter && f.ground_name !== groundFilter) return false;
      return true;
    });

    // Count usage per ground per division group
    const takenPremier = {}, takenDiv12 = {}, takenDiv3 = {};
    filtered.forEach(f => {
      if (!f.ground_name) return;
      const div = (f.division_name || '').toLowerCase();
      if (div.includes('premier'))         takenPremier[f.ground_name] = (takenPremier[f.ground_name]||0)+1;
      else if (div.includes('division 3')) takenDiv3[f.ground_name]    = (takenDiv3[f.ground_name]||0)+1;
      else                                  takenDiv12[f.ground_name]   = (takenDiv12[f.ground_name]||0)+1;
    });

    const groundRules = condArr.filter(c => c.condition_type === 'ground_rule');
    const groundBkgs  = condArr.filter(c => c.condition_type === 'ground_booking');

    // When month filter is active: totalSlots = actual available dates in that month × maxPD
    // (not the season total), because "unused" should be relative to that month's capacity
    const stats = gl.map(g => {
      const gr    = groundRules.find(r => r.value?.ground_id === g.id);
      const bk    = groundBkgs.find(gb => gb.value?.ground_id === g.id);
      const maxPD = gr?.value?.max_games_per_day ? parseInt(gr.value.max_games_per_day) : 1;

      let totalSlots;
      if (monthFilter) {
        // Count how many availability dates this ground has in the selected month
        // We don't have per-month avail data here, but we can infer from all fixtures
        // The games played in this month at this ground = the used slots
        // The AVAILABLE capacity in this month = number of Sundays in month × maxPD
        // Since we don't have per-month availability, use: games played + "left" inferred
        // Best approach: count total slots per month as avDates_in_month × maxPD
        // We already have avMap (total across season). Approximate by proportion.
        // Actually: just show used vs unknown-total, compute "unused" as slots used in that month
        const usedThisMonth = (takenPremier[g.name]||0) + (takenDiv12[g.name]||0) + (takenDiv3[g.name]||0);
        // For month view: totalSlots = estimated slots in month = roughly avMap[g.id]/4 months × maxPD
        // But we can't know exact without fetching per-month availability
        // Show only what we know: how many games happened in this month at this ground
        totalSlots = usedThisMonth; // will show 0 "left" which is fine — we show "used" data
      } else if (bk) {
        totalSlots = bk.value?.games_count || 0;
      } else {
        totalSlots = (avMap[g.id] || 0) * maxPD;
      }

      return {
        name:           g.name,
        type:           g.ground_type || 'standard',
        avDates:        avMap[g.id] || 0,
        maxPD,
        totalSlots,
        takenByPremier: takenPremier[g.name] || 0,
        takenByDiv12:   takenDiv12[g.name]   || 0,
        takenByDiv3:    takenDiv3[g.name]     || 0,
        isExclusive:    !!bk,
        monthFilter:    !!monthFilter,
      };
    }).filter(g => g.totalSlots > 0 || g.avDates > 0 || g.takenByPremier > 0 || g.takenByDiv12 > 0 || g.takenByDiv3 > 0);

    setGroundStats(stats);
  };

  // Recompute when filter changes
  useEffect(() => {
    if (allFixtures.length > 0 && grounds.length > 0 && conditions.length > 0) {
      buildGroundStats(allFixtures, conditions, grounds, availMap, filterMonth || undefined, filterGround || undefined);
    }
  }, [filterMonth, filterGround, allFixtures]);

  // ── Derived preflight ────────────────────────────────────────────────────
  const condByType = (type) => conditions.filter(c => c.condition_type === type);
  const playDays    = condByType('game_day')[0]?.value?.days || [];
  const timeSlots   = condByType('time_slot')[0]?.value?.slots || [];
  const groundRules = condByType('ground_rule');
  const groundBkgs  = condByType('ground_booking');
  const tglConds    = condByType('team_ground_limit');
  const c7Overrides = condByType('team_specific').filter(c => c.value?.override_type === 'Time');
  const fixedDates  = condByType('fixed_game_date');
  const freeText    = condByType('free_text');

  const warnings = [], errors = [];
  if (selSeries) {
    if (!playDays.length)  errors.push('C1: Play days not configured');
    if (!timeSlots.length) errors.push('C2: Time slots not configured');
    if (teams.length < 2)  errors.push('Not enough teams (need ≥ 2)');
    const totalAv = Object.values(availMap).reduce((a,b) => a+b, 0);
    if (totalAv === 0) errors.push('No ground availability set — add availability dates first');
    matrixInfo.forEach(m => { if (!m.hasMatrix) warnings.push(`${m.division.name}: no matrix — full round-robin`); });
    if (freeText.length > 0) warnings.push(`${freeText.length} free-text condition(s) not auto-enforced`);
    groundStats.forEach(g => {
      const left = g.totalSlots - g.takenByPremier - g.takenByDiv12 - (g.takenByDiv3||0);
      if (!g.monthFilter && left < 0) errors.push(`${g.name}: over-capacity (${g.takenByPremier+g.takenByDiv12+(g.takenByDiv3||0)} used vs ${g.totalSlots} slots)`);
    });
  }

  // Pool format → seeded games
  const poolFormatCond = conditions.find(c => c.condition_type === 'pool_format' && c.value?.seeded_game === true);
  const perDivExpected = divisions.map(d => {
    const gpt = d.games_per_team || 9;
    const divTeams = teams.filter(t => t.division_id === d.id);
    const mat = matrixInfo.find(m => m.division.id === d.id);
    const league = mat?.hasMatrix && mat.matrixCount > 0
      ? Math.round(mat.matrixCount / 2)
      : Math.floor((gpt * divTeams.length) / 2);
    let seeded = 0;
    const seededGames = [];
    if (poolFormatCond && poolFormatCond.value?.division_id === d.id) {
      const poolLetter = poolFormatCond.value?.pool || 'A';
      const poolTeams = divTeams.filter(t => t.pool === poolLetter);
      const n = poolTeams.length;
      if (n % 2 !== 0 && n >= 3) {
        seeded = (n - 1) / 2;
        for (let i = 0; i < seeded; i++)
          seededGames.push(`Pool ${poolLetter} Rank ${i+2} vs Pool ${poolLetter} Rank ${n-i}`);
      }
    }
    return { name:d.name, league, seeded, seededGames, teams:divTeams.length };
  });
  const expectedLeague = perDivExpected.reduce((s,d) => s+d.league, 0);
  const expectedSeeded = perDivExpected.reduce((s,d) => s+d.seeded, 0);
  const expectedTotal  = expectedLeague + expectedSeeded;
  const totalAvailSlots = groundStats.filter(g => !g.monthFilter).reduce((s,g) => s+g.totalSlots, 0);

  // Months available from all fixtures
  const availMonths = [...new Set(allFixtures.map(f => String(f.match_date||'').split('T')[0].substring(0,7)).filter(Boolean))].sort();
  const allGroundNames = [...new Set(allFixtures.map(f => f.ground_name).filter(Boolean))].sort();

  // ── Generate ─────────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (errors.length > 0) { toast.error('Fix errors before generating'); return; }
    setGenerating(true); setGenPhase('generating'); setGenLog([]); setGenResult(null);
    const addLog = (msg, type='info') => setGenLog(prev => [...prev, { msg, type, id:Date.now()+Math.random() }]);
    try {
      addLog('Starting schedule generation…');
      const token = localStorage.getItem('michca_token');
      const res = await fetch(`/api/fixtures/generate/${selSeries}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          divisionOrder: divOrder,
          monthlyTargets,
          groundPriority,
          seededReservation: { month: seededMonth, slotsToReserve: seededReserveCount, floorDate: seededFloorDate, poolGamesRequired: poolAGamesReq },
          conflictStrategy,
        })
      });
      const data = await res.json();
      (data.log || []).forEach(e => addLog(e.msg, e.type || 'info'));
      data.errors?.forEach(e => addLog(`ERROR (${e.division}): ${e.error}`, 'error'));
      data.conflicts?.forEach(c => addLog(`CONFLICT: ${c.home} vs ${c.away} — ${c.reason||'no slot'}`, 'warn'));
      data.warnings?.forEach(w => addLog(`WARNING: ${w}`, 'warn'));
      const nC = data.conflicts?.length||0, nE = data.errors?.length||0;
      addLog(`${data.total_fixtures||0} fixtures · ${nC} conflicts · ${nE} errors`, nE?'error':nC?'warn':'ok');
      setGenResult(data); setGenPhase('done');
      if (selSeries) await loadGroundStats(selSeries, conditions, grounds, availMap);
    } catch (err) { addLog(`Fatal: ${err.message}`, 'error'); setGenPhase('done'); }
    finally { setGenerating(false); }
  };

  const downloadExcel = async (combined) => {
    const url = combined
      ? `/api/fixtures/export/excel/combined?series_id=${selSeries}`
      : `/api/fixtures/export/excel?series_id=${selSeries}`;
    try {
      const token = localStorage.getItem('michca_token');
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { toast.error('Export failed'); return; }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `schedule_${combined?'combined_':''}${selSeries}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (err) { toast.error('Download failed: ' + err.message); }
  };

  const hasErrors = errors.length > 0;
  const isFiltered = !!(filterGround || filterMonth);

  return (
    <div style={{ maxWidth:1200, margin:'0 auto', padding:'0 20px 60px' }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:16, padding:'20px 0 20px',
          borderBottom:'1px solid var(--border)', marginBottom:24 }}>
        <div style={{ flex:1 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
            <button onClick={() => navigate('/schedule')} className="btn btn-ghost" style={{ fontSize:11, padding:'4px 10px' }}>
              ← Schedule
            </button>
            <div style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:10, fontWeight:800,
                letterSpacing:'1px', textTransform:'uppercase', padding:'3px 9px', borderRadius:20,
                background:'rgba(0,200,150,.1)', color:'var(--accent)', border:'1px solid rgba(0,200,150,.2)' }}>
              ⚡ Schedule Generator
            </div>
          </div>
          <div style={{ fontSize:22, fontWeight:900, color:'var(--text)' }}>Generate League Schedule</div>
          <div style={{ fontSize:12, color:'var(--mid)', marginTop:2 }}>Pre-flight checks · Ground slot analysis · Generate · Review errors & warnings</div>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
          <label style={{ fontSize:10, fontWeight:700, color:'var(--dim)', textTransform:'uppercase' }}>Series</label>
          <select className="input select" style={{ width:200, fontSize:13, fontWeight:600 }}
            value={selSeries} onChange={e => setSelSeries(e.target.value)}>
            {seriesList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          {seriesObj && <div style={{ fontSize:10, color:'var(--dim)' }}>{divisions.length} divisions · {teams.length} teams</div>}
        </div>
      </div>

      {loadingMeta ? (
        <div style={{ display:'flex', justifyContent:'center', padding:60 }}><Spinner size={28}/></div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>

          {/* ── LEFT: Preflight ──────────────────────────────────────── */}
          <div>
            <Section icon="🔍" title="Pre-flight Check"
              badge={hasErrors ? `${errors.length} error${errors.length>1?'s':''}` : warnings.length ? `${warnings.length} warning${warnings.length>1?'s':''}` : 'Ready'}
              badgeColor={hasErrors ? 'var(--red)' : warnings.length ? 'var(--gold)' : 'var(--accent)'}>

              {/* ── Colorful summary cards ── */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:16 }}>
                {[
                  {
                    label:'Expected', value:expectedTotal, color:'#00C896', bg:'rgba(0,200,150,.08)',
                    border:'rgba(0,200,150,.25)', icon:'📋',
                    sub: expectedSeeded > 0 ? `${expectedLeague} league + ${expectedSeeded} seeded` : `${expectedLeague} league`
                  },
                  {
                    label:'Avail Slots', value:totalAvailSlots, icon:'🏟',
                    color: totalAvailSlots >= expectedTotal ? '#4A90D9' : '#EF4444',
                    bg: totalAvailSlots >= expectedTotal ? 'rgba(74,144,217,.08)' : 'rgba(239,68,68,.08)',
                    border: totalAvailSlots >= expectedTotal ? 'rgba(74,144,217,.25)' : 'rgba(239,68,68,.25)',
                    sub: totalAvailSlots >= expectedTotal ? `+${totalAvailSlots-expectedTotal} surplus` : `${expectedTotal-totalAvailSlots} short ⚠`
                  },
                  {
                    label:'Teams', value:teams.length, color:'#9B59B6', bg:'rgba(155,89,182,.08)',
                    border:'rgba(155,89,182,.25)', icon:'👥',
                    sub:`across ${divisions.length} division${divisions.length!==1?'s':''}`
                  },
                ].map(s => (
                  <div key={s.label} style={{ background:s.bg, border:`1px solid ${s.border}`,
                      borderRadius:10, padding:'12px 14px', position:'relative', overflow:'hidden' }}>
                    <div style={{ position:'absolute', top:8, right:10, fontSize:18, opacity:.3 }}>{s.icon}</div>
                    <div style={{ fontSize:9, color:s.color, fontWeight:800, textTransform:'uppercase', letterSpacing:'.5px', marginBottom:4 }}>{s.label}</div>
                    <div style={{ fontSize:26, fontWeight:900, color:s.color, fontFamily:"'Roboto Mono',monospace", lineHeight:1 }}>{s.value}</div>
                    <div style={{ fontSize:9, color:s.color, opacity:.7, marginTop:4 }}>{s.sub}</div>
                  </div>
                ))}
              </div>

              {/* Per division expected */}
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:10, fontWeight:700, color:'var(--dim)', textTransform:'uppercase', letterSpacing:'.4px', marginBottom:6 }}>Expected by Division</div>
                {perDivExpected.map(d => (
                  <div key={d.name} style={{ borderBottom:'1px solid var(--border)', padding:'5px 0' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:12 }}>
                      <span style={{ color:'var(--text)', fontWeight:600 }}>{d.name}</span>
                      <span style={{ color:'var(--dim)', fontSize:11 }}>{d.teams} teams</span>
                      <span style={{ fontFamily:"'Roboto Mono',monospace", color:'var(--accent)', fontWeight:700 }}>
                        {d.league}{d.seeded > 0 ? ` + ${d.seeded} seeded` : ''} games
                      </span>
                    </div>
                    {d.seededGames?.map((g,i) => (
                      <div key={i} style={{ fontSize:10, color:'var(--blue)', paddingLeft:8, marginTop:2, fontStyle:'italic' }}>
                        ↳ {g}
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              {/* Condition checks */}
              <CheckRow status={playDays.length?'ok':'error'} label="C1 · Play Days"
                value={playDays.length ? playDays.join(', ') : 'Not configured'} href={!playDays.length?'/conditions':undefined} />
              <CheckRow status={timeSlots.length?'ok':'error'} label="C2 · Global Time Slots"
                value={timeSlots.length ? timeSlots.map(fmt12).join(', ') : 'Not configured'}
                sub={c7Overrides.length ? `C7 overrides: ${c7Overrides.map(c=>c.value?.division_name).join(', ')}` : undefined}
                href={!timeSlots.length?'/conditions':undefined} />
              {divisions.map(d => {
                const gpt = d.games_per_team||9;
                return <CheckRow key={d.id} status="ok" label={`C3 · ${d.name} H/A`}
                  value={`${Math.ceil(gpt/2)}H / ${Math.floor(gpt/2)}A`} sub="Auto from games/team" />;
              })}
              {(() => {
                const byeMin = condByType('bye_min')[0]?.value?.min;
                const byeMax = condByType('bye_max')[0]?.value?.max;
                return <CheckRow status={byeMin!=null||byeMax!=null?'ok':'info'} label="C4 · Bye Rules"
                  value={byeMin!=null||byeMax!=null ? `Min ${byeMin??'—'} · Max ${byeMax??'—'}` : 'Defaults (min 1, max 2)'}
                  href={byeMin==null&&byeMax==null?'/conditions':undefined} />;
              })()}
              <CheckRow status="ok" label="C5 · Premier Grounds"
                value={grounds.filter(g=>g.ground_type==='premier').map(g=>g.name).join(', ')||'None set'}
                sub="Premier division only — live from ground_type" />
              <CheckRow status={tglConds.length?'ok':'info'} label="C8 · Team-Ground Limits"
                value={tglConds.length ? `${tglConds.length} limit(s) configured` : 'None — Troy requires C8 listing'} />
              {groundBkgs.map((gb,i) => {
                const v = gb.value||{};
                const g = grounds.find(gr=>gr.id===v.ground_id);
                const avCount = availMap[v.ground_id]||0;
                const ok = avCount >= v.games_count;
                return <CheckRow key={i} status={ok?'ok':'warn'} label={`C10 · ${v.team_short} @ ${g?.name}`}
                  value={`${v.games_count} exclusive home games · ${avCount} dates available`}
                  href={!ok?'/availability':undefined} />;
              })}
              {fixedDates.map((fd,i) => {
                const v = fd.value||{};
                return <CheckRow key={i} status="ok" label="C11 · Fixed Date"
                  value={`${v.division_name} must play on ${v.date}`} />;
              })}
              <CheckRow status={matrixInfo.every(m=>m.hasMatrix)?'ok':'warn'} label="Matchup Matrix"
                value={matrixInfo.every(m=>m.hasMatrix) ? 'All divisions have matrix' :
                  `No matrix: ${matrixInfo.filter(m=>!m.hasMatrix).map(m=>m.division.name).join(', ')}`}
                href={matrixInfo.some(m=>!m.hasMatrix)?'/matrix':undefined} />
            </Section>

            {/* Errors & Warnings */}
            {(errors.length > 0 || warnings.length > 0) && (
              <Section icon="⚠️" title="Errors & Warnings"
                badge={`${errors.length} error${errors.length!==1?'s':''} · ${warnings.length} warning${warnings.length!==1?'s':''}`}
                badgeColor={errors.length ? 'var(--red)' : 'var(--gold)'}>
                {errors.map((e,i) => (
                  <div key={i} style={{ display:'flex', gap:8, padding:'6px 0', borderBottom:'1px solid var(--border)', fontSize:12 }}>
                    <span style={{ color:'var(--red)', fontWeight:700 }}>✕</span>
                    <span style={{ color:'var(--red)' }}>{e}</span>
                  </div>
                ))}
                {warnings.map((w,i) => (
                  <div key={i} style={{ display:'flex', gap:8, padding:'6px 0', borderBottom:'1px solid var(--border)', fontSize:12 }}>
                    <span style={{ color:'var(--gold)', fontWeight:700 }}>⚠</span>
                    <span style={{ color:'var(--gold)' }}>{w}</span>
                  </div>
                ))}
              </Section>
            )}
          </div>

          {/* ── RIGHT: Ground Slot Analysis + Generate ───────────────── */}
          <div>
            {/* Ground Slot Analysis */}
            <Section icon="🏟" title="Ground Slot Analysis"
              badge={isFiltered ? '🔍 Filtered' : `${groundStats.length} grounds`}
              badgeColor={isFiltered ? 'var(--gold)' : 'var(--blue)'} defaultOpen={true}>

              {/* Filter controls */}
              <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap', alignItems:'flex-end' }}>
                <div>
                  <div style={{ fontSize:9, fontWeight:700, color:'var(--dim)', textTransform:'uppercase', letterSpacing:'.4px', marginBottom:3 }}>Filter by Ground</div>
                  <select className="input select" style={{ fontSize:11 }}
                    value={filterGround} onChange={e => setFilterGround(e.target.value)}>
                    <option value="">All Grounds</option>
                    {allGroundNames.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <div style={{ fontSize:9, fontWeight:700, color:'var(--dim)', textTransform:'uppercase', letterSpacing:'.4px', marginBottom:3 }}>Filter by Month</div>
                  <select className="input select" style={{ fontSize:11 }}
                    value={filterMonth} onChange={e => setFilterMonth(e.target.value)}>
                    <option value="">All Months</option>
                    {availMonths.map(m => {
                      const [y, mo] = m.split('-');
                      const label = new Date(y, parseInt(mo)-1, 1).toLocaleDateString('en-US', { month:'long', year:'numeric' });
                      return <option key={m} value={m}>{label}</option>;
                    })}
                  </select>
                </div>
                {isFiltered && (
                  <button className="btn btn-ghost btn-sm" style={{ fontSize:10, alignSelf:'flex-end' }}
                    onClick={() => { setFilterGround(''); setFilterMonth(''); }}>
                    ✕ Clear filters
                  </button>
                )}
              </div>

              {/* Context label */}
              {filterMonth && (
                <div style={{ fontSize:11, color:'var(--gold)', background:'rgba(245,166,35,.08)',
                    border:'1px solid rgba(245,166,35,.2)', borderRadius:6, padding:'6px 10px', marginBottom:12 }}>
                  📅 Showing slot usage for {new Date(filterMonth+'-02').toLocaleDateString('en-US',{month:'long',year:'numeric'})} only.
                  Numbers show actual games played this month per ground.
                </div>
              )}

              {loadingStats ? (
                <div style={{ display:'flex', justifyContent:'center', padding:20 }}><Spinner /></div>
              ) : groundStats.length === 0 ? (
                <div style={{ fontSize:12, color:'var(--dim)', padding:'12px 0', textAlign:'center' }}>
                  No data — generate a schedule first to see slot usage.
                </div>
              ) : (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:10 }}>
                  {groundStats.map(g => {
                    const isGroundFiltered = !filterGround || filterGround === g.name;
                    const dimmed = isFiltered && !isGroundFiltered;
                    const highlighted = isFiltered && isGroundFiltered;
                    return (
                      <GroundCard key={g.name} g={g}
                        filtered={highlighted} filterType={filterMonth ? 'month' : filterGround ? 'ground' : null}
                        dimmed={dimmed} />
                    );
                  })}
                </div>
              )}

              {/* Summary totals (when no filter or month filter) */}
              {!filterGround && groundStats.length > 0 && (
                <div style={{ marginTop:14, padding:'10px 14px', background:'var(--surface)',
                    borderRadius:8, border:'1px solid var(--border)' }}>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:8, textAlign:'center' }}>
                    {[
                      { label:'Total Slots', value:groundStats.reduce((s,g)=>s+g.totalSlots,0), color:'var(--mid)' },
                      { label:'By Premier', value:groundStats.reduce((s,g)=>s+g.takenByPremier,0), color:'#F5A623' },
                      { label:'By Div 1/2', value:groundStats.reduce((s,g)=>s+g.takenByDiv12,0), color:'#4A90D9' },
                      { label:'By Div 3', value:groundStats.reduce((s,g)=>s+(g.takenByDiv3||0),0), color:'#7B68EE' },
                      { label:'Unused',
                        value:groundStats.reduce((s,g)=>s+g.totalSlots-g.takenByPremier-g.takenByDiv12-(g.takenByDiv3||0),0),
                        color:'var(--accent)' },
                    ].map(s => (
                      <div key={s.label}>
                        <div style={{ fontSize:18, fontWeight:800, color:s.color, fontFamily:"'Roboto Mono',monospace" }}>{s.value}</div>
                        <div style={{ fontSize:9, color:'var(--dim)', fontWeight:600, textTransform:'uppercase' }}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Section>

            {/* ── CONTROL PANEL ─────────────────────────────────────── */}
            <Section icon="🎛" title="Schedule Control Panel"
              badge="Pilot Controls" badgeColor="var(--blue)" defaultOpen={showControlPanel}>

              <div style={{ fontSize:11, color:'var(--mid)', marginBottom:16, lineHeight:1.6 }}>
                Adjust these controls to tune how the schedule is generated. Changes take effect on the next generation run.
              </div>

              {/* ── Division Order ─────────────────────────────────────── */}
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:10, fontWeight:800, color:'var(--text)', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:8, display:'flex', alignItems:'center', gap:6 }}>
                  <span>📋 Division Scheduling Order</span>
                  <span style={{ fontSize:9, color:'var(--dim)', fontWeight:400, textTransform:'none' }}>— drag to reorder · first division gets first pick of slots</span>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                  {divOrder.map((div, idx) => {
                    const colors = { 'Premier':'#F5A623','Division 3':'#7B68EE','Division 1':'#4A90D9','Division 2':'#9B59B6' };
                    const col = colors[div] || 'var(--mid)';
                    const isPremier = div === 'Premier';
                    return (
                      <div key={div}
                        draggable={!isPremier}
                        onDragStart={() => !isPremier && setDragDivIdx(idx)}
                        onDragOver={e => { e.preventDefault(); }}
                        onDrop={() => {
                          if (dragDivIdx === null || dragDivIdx === idx) return;
                          const newOrder = [...divOrder];
                          const [moved] = newOrder.splice(dragDivIdx, 1);
                          newOrder.splice(idx, 0, moved);
                          // Premier must always stay first
                          const pIdx = newOrder.indexOf('Premier');
                          if (pIdx > 0) { newOrder.splice(pIdx,1); newOrder.unshift('Premier'); }
                          setDivOrder(newOrder);
                          setDragDivIdx(null);
                        }}
                        onDragEnd={() => setDragDivIdx(null)}
                        style={{
                          display:'flex', alignItems:'center', gap:10, padding:'8px 12px',
                          background: dragDivIdx === idx ? `color-mix(in srgb, ${col} 20%, transparent)` : `color-mix(in srgb, ${col} 8%, transparent)`,
                          border: `1px solid color-mix(in srgb, ${col} 30%, transparent)`,
                          borderRadius:8, cursor: isPremier ? 'default' : 'grab',
                          transition:'background .15s',
                        }}>
                        <span style={{ fontSize:14, color:'var(--dim)', minWidth:16 }}>{isPremier ? '🔒' : '⠿'}</span>
                        <span style={{ fontSize:13, fontWeight:800, color:col, minWidth:16 }}>#{idx+1}</span>
                        <span style={{ fontSize:12, fontWeight:700, color:'var(--text)', flex:1 }}>{div}</span>
                        {isPremier && <span style={{ fontSize:9, color:'var(--dim)', fontStyle:'italic' }}>locked first · locks 12:30+16:00 slots</span>}
                        {div==='Division 3' && idx <= 2 && <span style={{ fontSize:9, color:'#7B68EE', background:'rgba(123,104,238,.12)', padding:'2px 6px', borderRadius:10 }}>recommended 2nd</span>}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ── Monthly Targets ────────────────────────────────────── */}
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:10, fontWeight:800, color:'var(--text)', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:8, display:'flex', alignItems:'center', gap:6 }}>
                  <span>📅 Monthly Game Targets</span>
                  <span style={{ fontSize:9, color:'var(--dim)', fontWeight:400, textTransform:'none' }}>— adjust how many games each division plays per month</span>
                </div>

                {/* ── Shared budget bar per month ─────────────────────── */}
                {(() => {
                  const MONTHS = [['2026-05','may','May'],['2026-06','jun','Jun'],['2026-07','jul','Jul'],['2026-08','aug','Aug']];
                  // Total raw slots per month (from ground availability)
                  // Premier uses ~2/3 of premier ground slots (12:30+16:00 only)
                  // Remaining = shared pool for Div1+Div2+Div3
                  const totalPool = slotInventory; // { '2026-05': N, ... }
                  const hasInventory = Object.keys(totalPool).length > 0;

                  return (
                    <div style={{ marginBottom:16 }}>
                      <div style={{ fontSize:9, fontWeight:700, color:'var(--dim)', textTransform:'uppercase', letterSpacing:'.4px', marginBottom:6 }}>
                        Shared Slot Budget — All Divisions Combined
                      </div>
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:6 }}>
                        {MONTHS.map(([mkey, mslug, mlabel]) => {
                          const rawSlots    = totalPool[mkey] || 0;
                          // Premier allocation: premier division target for this month
                          const premierAlloc = monthlyTargets['Premier']?.[mslug] || 0;
                          // Available for Div1+2+3 = raw - premier
                          const available   = Math.max(0, rawSlots - premierAlloc);
                          // Sum of all non-premier div targets for this month
                          const allocated   = ['Division 3','Division 1','Division 2']
                            .reduce((s,d) => s + (monthlyTargets[d]?.[mslug]||0), 0);
                          const remaining   = available - allocated;
                          const isOver      = remaining < 0;
                          const isTight     = remaining >= 0 && remaining <= 3;
                          const statusColor = isOver ? '#EF4444' : isTight ? '#F59E0B' : '#22C55E';
                          const pct         = available > 0 ? Math.min(100, Math.round((allocated/available)*100)) : 0;

                          return (
                            <div key={mkey} style={{
                              padding:'8px 10px', borderRadius:8,
                              background: isOver ? 'rgba(239,68,68,.08)' : isTight ? 'rgba(245,158,11,.08)' : 'rgba(34,197,94,.06)',
                              border: `1px solid ${isOver ? 'rgba(239,68,68,.3)' : isTight ? 'rgba(245,158,11,.3)' : 'rgba(34,197,94,.2)'}`,
                            }}>
                              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:4 }}>
                                <span style={{ fontSize:10, fontWeight:800, color:'var(--text)' }}>{mlabel}</span>
                                <span style={{ fontSize:9, fontFamily:"'Roboto Mono',monospace", color: statusColor, fontWeight:800 }}>
                                  {isOver ? `+${Math.abs(remaining)} OVER` : `${remaining} free`}
                                </span>
                              </div>
                              {/* Budget bar */}
                              <div style={{ height:5, background:'var(--border)', borderRadius:3, overflow:'hidden', marginBottom:4 }}>
                                <div style={{ height:'100%', width:`${pct}%`,
                                  background: isOver ? '#EF4444' : isTight ? '#F59E0B' : '#22C55E',
                                  borderRadius:3, transition:'width .2s' }} />
                              </div>
                              {hasInventory ? (
                                <div style={{ fontSize:9, color:'var(--dim)' }}>
                                  <span style={{ color:'var(--text)', fontWeight:700 }}>{allocated}</span>/{available} slots used
                                </div>
                              ) : (
                                <div style={{ fontSize:9, color:'var(--dim)', fontStyle:'italic' }}>loading…</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {['2026-05','2026-06','2026-07','2026-08'].some(m => {
                        const mslug = {' 2026-05':'may','2026-05':'may','2026-06':'jun','2026-07':'jul','2026-08':'aug'}[m];
                        const rawSlots = totalPool[m]||0;
                        const premierAlloc = monthlyTargets['Premier']?.[mslug]||0;
                        const available = Math.max(0, rawSlots - premierAlloc);
                        const allocated = ['Division 3','Division 1','Division 2'].reduce((s,d)=>s+(monthlyTargets[d]?.[mslug]||0),0);
                        return allocated > available;
                      }) && (
                        <div style={{ marginTop:6, padding:'6px 10px', borderRadius:6,
                            background:'rgba(239,68,68,.08)', border:'1px solid rgba(239,68,68,.2)',
                            fontSize:10, color:'var(--red)' }}>
                          ⚠ One or more months are over-allocated. The generator will use available slots but some games may conflict.
                          Reduce targets in the over-budget months or increase ground availability.
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* ── Per-division sliders ────────────────────────────── */}
                {divOrder.map(div => {
                  const colors = { 'Premier':'#F5A623','Division 3':'#7B68EE','Division 1':'#4A90D9','Division 2':'#9B59B6' };
                  const col = colors[div] || 'var(--mid)';
                  const t = monthlyTargets[div] || { may:24, jun:24, jul:24, aug:26 };
                  const total = t.may + t.jun + t.jul + t.aug;
                  const MONTHS = [['2026-05','may','May'],['2026-06','jun','Jun'],['2026-07','jul','Jul'],['2026-08','aug','Aug']];

                  return (
                    <div key={div} style={{ marginBottom:14, padding:'10px 12px',
                      background:`color-mix(in srgb, ${col} 6%, transparent)`,
                      border:`1px solid color-mix(in srgb, ${col} 20%, transparent)`, borderRadius:8 }}>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                        <span style={{ fontSize:11, fontWeight:800, color:col }}>{div}</span>
                        <span style={{ fontSize:10, color:'var(--dim)' }}>
                          total: <strong style={{ color:'var(--text)' }}>{total}</strong>
                          {div !== 'Premier' && (
                            <span style={{ marginLeft:8, fontSize:9, color:'var(--dim)' }}>
                              (expected: {perDivExpected.find(d=>d.name===div)?.league || '—'})
                            </span>
                          )}
                        </span>
                      </div>
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8 }}>
                        {MONTHS.map(([mkey, mslug, mlabel]) => {
                          const val = t[mslug];
                          // Compute available slots for THIS division in THIS month
                          // = total raw slots - premier alloc - OTHER divisions' targets
                          const rawSlots    = slotInventory[mkey] || 0;
                          const premierAlloc = monthlyTargets['Premier']?.[mslug] || 0;
                          const poolAvail   = Math.max(0, rawSlots - premierAlloc);
                          const otherDivs   = ['Premier','Division 3','Division 1','Division 2']
                            .filter(d => d !== div && d !== 'Premier');
                          const otherAlloc  = (div === 'Premier' ? [] : ['Division 3','Division 1','Division 2'].filter(d=>d!==div))
                            .reduce((s,d) => s + (monthlyTargets[d]?.[mslug]||0), 0);
                          // Max this division can get in this month
                          const maxAvail    = div === 'Premier' ? 50 : Math.max(val, poolAvail - otherAlloc);
                          const slotsFree   = div === 'Premier' ? null : poolAvail - otherAlloc - val;
                          const isSlotOver  = slotsFree !== null && slotsFree < 0;
                          const isSlotTight = slotsFree !== null && slotsFree >= 0 && slotsFree <= 2;
                          const slotColor   = isSlotOver ? '#EF4444' : isSlotTight ? '#F59E0B' : '#22C55E';
                          const maxSlider   = Math.min(50, Math.max(val, poolAvail));
                          const pct         = maxSlider > 0 ? Math.round((val/maxSlider)*100) : 0;

                          return (
                            <div key={mslug}>
                              {/* Month label + current value */}
                              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:2 }}>
                                <span style={{ fontSize:9, color:'var(--dim)', fontWeight:700 }}>{mlabel}</span>
                                <span style={{ fontSize:11, fontFamily:"'Roboto Mono',monospace", color:col, fontWeight:900 }}>{val}</span>
                              </div>
                              {/* Slider */}
                              <input type="range" min={0} max={maxSlider} value={val}
                                style={{ width:'100%', accentColor: isSlotOver ? '#EF4444' : col, height:4, cursor:'pointer' }}
                                onChange={e => {
                                  const newVal = parseInt(e.target.value);
                                  setMonthlyTargets(prev => ({
                                    ...prev,
                                    [div]: { ...prev[div], [mslug]: newVal }
                                  }));
                                }}
                              />
                              {/* Filled bar */}
                              <div style={{ height:3, background:`color-mix(in srgb, ${col} 15%, transparent)`,
                                  borderRadius:2, marginTop:2, position:'relative' }}>
                                <div style={{ position:'absolute', left:0, top:0, height:'100%',
                                  width:`${pct}%`, background: isSlotOver ? '#EF4444' : col,
                                  borderRadius:2, transition:'width .15s' }} />
                              </div>
                              {/* Available slots hint */}
                              {div !== 'Premier' && slotInventory[mkey] > 0 && (
                                <div style={{ marginTop:3, fontSize:8, color: isSlotOver ? '#EF4444' : isSlotTight ? '#F59E0B' : 'var(--dim)',
                                    fontWeight: (isSlotOver||isSlotTight) ? 700 : 400 }}>
                                  {isSlotOver
                                    ? `${Math.abs(slotsFree)} over budget`
                                    : isSlotTight
                                    ? `only ${slotsFree} slot${slotsFree!==1?'s':''} left`
                                    : `${slotsFree} slots free`}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* ── Ground Priority ────────────────────────────────────── */}
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:10, fontWeight:800, color:'var(--text)', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:8, display:'flex', alignItems:'center', gap:6 }}>
                  <span>🏟 Ground Fill Priority</span>
                  <span style={{ fontSize:9, color:'var(--dim)', fontWeight:400, textTransform:'none' }}>— drag to reorder · filled top-to-bottom by post-fill</span>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
                  {groundPriority.filter(g => {
                    const gr = grounds.find(gg => gg.name === g);
                    return !gr || gr.ground_type === 'premier' || ['Northville','Belle-Isle','Canton','Clinton','Trombly'].includes(g);
                  }).concat(groundPriority.filter(g => {
                    const gr = grounds.find(gg => gg.name === g);
                    return gr && gr.ground_type !== 'premier' && !['Northville','Belle-Isle','Canton','Clinton','Trombly'].includes(g);
                  })).length > 0 && groundPriority.map((gName, idx) => {
                    const gr = grounds.find(gg => gg.name === gName);
                    const isPremier = gr?.ground_type === 'premier';
                    const isWorst = ['Canton','Clinton','Trombly'].includes(gName);
                    const col = isPremier ? '#F5A623' : isWorst ? 'var(--dim)' : '#4A90D9';
                    return (
                      <div key={gName}
                        draggable
                        onDragStart={() => setDragGndIdx(idx)}
                        onDragOver={e => e.preventDefault()}
                        onDrop={() => {
                          if (dragGndIdx === null || dragGndIdx === idx) return;
                          const arr = [...groundPriority];
                          const [moved] = arr.splice(dragGndIdx, 1);
                          arr.splice(idx, 0, moved);
                          setGroundPriority(arr);
                          setDragGndIdx(null);
                        }}
                        onDragEnd={() => setDragGndIdx(null)}
                        style={{
                          display:'flex', alignItems:'center', gap:8, padding:'5px 10px',
                          background: dragGndIdx===idx ? `color-mix(in srgb, ${col} 20%, transparent)` : 'var(--surface)',
                          border:`1px solid ${isPremier ? 'rgba(245,166,35,.25)' : 'var(--border)'}`,
                          borderRadius:6, cursor:'grab',
                        }}>
                        <span style={{ fontSize:11, color:'var(--dim)', minWidth:14 }}>⠿</span>
                        <span style={{ fontSize:10, color:'var(--dim)', minWidth:18, fontFamily:"'Roboto Mono',monospace" }}>#{idx+1}</span>
                        <span style={{ fontSize:11, fontWeight:700, color:'var(--text)', flex:1 }}>{gName}</span>
                        {isPremier && <span style={{ fontSize:9, padding:'1px 5px', borderRadius:8, background:'rgba(245,166,35,.15)', color:'#F5A623', fontWeight:700 }}>PREMIER</span>}
                        {isWorst && <span style={{ fontSize:9, color:'var(--dim)', fontStyle:'italic' }}>last resort</span>}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ── Seeded Game Controls ───────────────────────────────── */}
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:10, fontWeight:800, color:'var(--text)', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:8 }}>
                  🎯 Seeded Game Controls <span style={{ fontSize:9, color:'#7B68EE', background:'rgba(123,104,238,.12)', padding:'2px 6px', borderRadius:10, fontWeight:600, textTransform:'none' }}>Div 3 Pool A</span>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  <div>
                    <label style={{ fontSize:9, fontWeight:700, color:'var(--dim)', textTransform:'uppercase', letterSpacing:'.3px' }}>Reserve Month</label>
                    <select className="input select" style={{ width:'100%', fontSize:11, marginTop:3 }}
                      value={seededMonth} onChange={e => setSeededMonth(e.target.value)}>
                      <option value="2026-05">May 2026</option>
                      <option value="2026-06">June 2026</option>
                      <option value="2026-07">July 2026</option>
                      <option value="2026-08">August 2026</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize:9, fontWeight:700, color:'var(--dim)', textTransform:'uppercase', letterSpacing:'.3px' }}>Slots to Reserve</label>
                    <input type="number" className="input" min={1} max={10} value={seededReserveCount}
                      style={{ width:'100%', fontSize:11, marginTop:3 }}
                      onChange={e => setSeededReserveCount(parseInt(e.target.value)||5)} />
                  </div>
                  <div>
                    <label style={{ fontSize:9, fontWeight:700, color:'var(--dim)', textTransform:'uppercase', letterSpacing:'.3px' }}>Floor Date (earliest)</label>
                    <div style={{ marginTop:3 }}>
                      <SingleDatePicker value={seededFloorDate} onChange={setSeededFloorDate} size="sm" width="100%" />
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize:9, fontWeight:700, color:'var(--dim)', textTransform:'uppercase', letterSpacing:'.3px' }}>Pool A Games Required</label>
                    <input type="number" className="input" min={1} max={12} value={poolAGamesReq}
                      style={{ width:'100%', fontSize:11, marginTop:3 }}
                      onChange={e => setPoolAGamesReq(parseInt(e.target.value)||8)} />
                  </div>
                </div>
                <div style={{ marginTop:8, fontSize:10, color:'var(--dim)', background:'rgba(123,104,238,.06)',
                    border:'1px solid rgba(123,104,238,.15)', borderRadius:6, padding:'6px 10px' }}>
                  ℹ {seededReserveCount} August slots will be reserved before league scheduling. Seeded games only schedule after {poolAGamesReq} Pool A league games are complete.
                </div>
              </div>

              {/* ── Conflict Strategy ──────────────────────────────────── */}
              <div style={{ marginBottom:8 }}>
                <div style={{ fontSize:10, fontWeight:800, color:'var(--text)', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:8 }}>
                  🔧 Conflict Resolution Strategy
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  {[
                    { value:'fill_premier_first', label:'Fill premier first', desc:'Assign conflict pairs to empty premier slots before standard grounds. Maximises premier ground usage.' },
                    { value:'fill_nearest_date', label:'Fill nearest date', desc:'Assign to the closest available date to where the pair would have naturally fallen in the schedule.' },
                    { value:'fill_preferred_month', label:'Fill preferred month', desc:'Try monthly target month first when resolving conflicts. Keeps distribution on target.' },
                  ].map(opt => (
                    <label key={opt.value} style={{ display:'flex', alignItems:'flex-start', gap:8, padding:'8px 10px', cursor:'pointer',
                        background: conflictStrategy===opt.value ? 'rgba(74,144,217,.08)' : 'var(--surface)',
                        border:`1px solid ${conflictStrategy===opt.value ? 'rgba(74,144,217,.3)' : 'var(--border)'}`,
                        borderRadius:7, transition:'background .15s' }}>
                      <input type="radio" name="conflictStrategy" value={opt.value}
                        checked={conflictStrategy===opt.value}
                        onChange={() => setConflictStrategy(opt.value)}
                        style={{ marginTop:2, accentColor:'#4A90D9', flexShrink:0 }} />
                      <div>
                        <div style={{ fontSize:11, fontWeight:700, color:'var(--text)' }}>{opt.label}</div>
                        <div style={{ fontSize:10, color:'var(--dim)', marginTop:2 }}>{opt.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Reset to defaults */}
              <button className="btn btn-ghost btn-sm" style={{ fontSize:10, marginTop:8 }}
                onClick={() => {
                  setDivOrder(['Premier','Division 1','Division 2','Division 3']);
                  setMonthlyTargets({ 'Premier':{ may:16,jun:18,jul:18,aug:18 }, 'Division 3':{ may:28,jun:26,jul:24,aug:20 }, 'Division 1':{ may:24,jun:28,jul:28,aug:28 }, 'Division 2':{ may:22,jun:26,jul:28,aug:32 } });
                  setGroundPriority(['Troy','Bloomer','Jayne','Lasky','Lyon Oaks','Sterling Heights','Northville','Belle-Isle','Redford','Murphy','Canton','Clinton','Trombly']);
                  setSeededMonth('2026-08'); setSeededFloorDate('2026-08-01');
                  setSeededReserveCount(5); setPoolAGamesReq(8);
                  setConflictStrategy('fill_premier_first');
                  toast.success('Controls reset to recommended defaults');
                }}>
                ↺ Reset to Recommended Defaults
              </button>
            </Section>

            {/* Generate */}
            <Section icon="⚡" title="Generate"
              badge={genPhase==='done'?(genResult?.conflicts?.length?'Conflicts':'Complete'):genPhase==='generating'?'Running…':'Ready'}
              badgeColor={genPhase==='done'?(genResult?.conflicts?.length?'var(--gold)':'var(--accent)'):genPhase==='generating'?'var(--blue)':'var(--mid)'}
              defaultOpen={true}>

              {hasErrors && (
                <div style={{ padding:'8px 12px', borderRadius:6, background:'rgba(239,68,68,.08)',
                    border:'1px solid rgba(239,68,68,.2)', fontSize:12, color:'var(--red)', marginBottom:12 }}>
                  ✕ Fix {errors.length} error(s) in Pre-flight before generating
                </div>
              )}

              <div style={{ display:'flex', gap:8, marginBottom:14 }}>
                <button className="btn btn-gold"
                  style={{ flex:1, justifyContent:'center', fontSize:13, fontWeight:700, padding:'10px 0' }}
                  onClick={handleGenerate} disabled={generating || hasErrors}>
                  {generating ? <><Spinner size={13}/> Generating…</> : genPhase==='done' ? '↺ Re-generate' : '⚡ Generate League Schedule'}
                </button>
                {genPhase==='done' && (
                  <button className="btn btn-ghost" style={{ fontSize:11 }} onClick={() => navigate('/schedule')}>
                    View →
                  </button>
                )}
              </div>

              {genPhase==='done' && genResult?.total_fixtures > 0 && (
                <>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginBottom:14 }}>
                    {[
                      { label:'Fixtures',  value:genResult.total_fixtures,     color:'var(--accent)' },
                      { label:'Conflicts', value:genResult.conflicts?.length||0, color:(genResult.conflicts?.length||0)>0?'var(--red)':'var(--accent)' },
                      { label:'Errors',    value:genResult.errors?.length||0,    color:(genResult.errors?.length||0)>0?'var(--red)':'var(--accent)' },
                      { label:'Warnings',  value:genResult.warnings?.length||0,  color:(genResult.warnings?.length||0)>0?'var(--gold)':'var(--accent)' },
                    ].map(s => (
                      <div key={s.label} style={{ textAlign:'center', padding:'8px 6px',
                          background:'var(--surface)', borderRadius:6, border:'1px solid var(--border)' }}>
                        <div style={{ fontSize:9, color:'var(--dim)', fontWeight:700, textTransform:'uppercase' }}>{s.label}</div>
                        <div style={{ fontSize:20, fontWeight:800, color:s.color, fontFamily:"'Roboto Mono',monospace" }}>{s.value}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display:'flex', gap:8, marginBottom:14 }}>
                    <button className="btn btn-primary" style={{ flex:1, fontSize:12 }} onClick={() => downloadExcel(false)}>
                      📊 By Division
                    </button>
                    <button className="btn btn-primary" style={{ flex:1, fontSize:12 }} onClick={() => downloadExcel(true)}>
                      📋 All Divisions
                    </button>
                  </div>
                </>
              )}

              {genPhase !== 'idle' && (
                <>
                  <div style={{ fontSize:10, fontWeight:700, color:'var(--dim)', textTransform:'uppercase', letterSpacing:'.4px', marginBottom:6 }}>
                    Generation Log
                  </div>
                  <div style={{ maxHeight:220, overflowY:'auto', background:'var(--surface)', borderRadius:6,
                      padding:'6px 10px', border:'1px solid var(--border)', marginBottom:14 }}>
                    {genLog.map((e,i) => <LogEntry key={i} entry={e} />)}
                    <div ref={logEndRef} />
                  </div>
                </>
              )}

              {(genResult?.conflicts?.length||0) > 0 && (
                <div style={{ background:'rgba(239,68,68,.05)', border:'1px solid rgba(239,68,68,.2)', borderRadius:8, padding:'12px 14px', marginBottom:14 }}>
                  <div style={{ fontSize:11, fontWeight:800, color:'var(--red)', marginBottom:10 }}>
                    ✕ {genResult.conflicts.length} Conflict{genResult.conflicts.length!==1?'s':''} — Resolution
                  </div>
                  <div style={{ maxHeight:160, overflowY:'auto', marginBottom:10 }}>
                    {genResult.conflicts.map((c,i) => (
                      <div key={i} style={{ fontSize:11, padding:'3px 0', borderBottom:'1px solid rgba(239,68,68,.1)', color:'var(--mid)' }}>
                        <span style={{ color:'var(--red)', fontWeight:700 }}>✕</span> {c.home} vs {c.away}
                        {c.reason && <span style={{ color:'var(--dim)', marginLeft:6 }}>— {c.reason}</span>}
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize:11, color:'var(--mid)', lineHeight:1.8 }}>
                    <strong style={{ color:'var(--text)' }}>Resolve:</strong><br/>
                    · <a href="/availability" style={{ color:'var(--blue)' }}>Add availability dates</a> — Murphy &amp; Redford have 0 dates<br/>
                    · Review C8 limits in <a href="/conditions" style={{ color:'var(--blue)' }}>Conditions</a><br/>
                    · Check <a href="/matrix" style={{ color:'var(--blue)' }}>matrix month restrictions</a>
                  </div>
                </div>
              )}

              {(genResult?.warnings?.length||0) > 0 && (
                <div style={{ background:'rgba(245,166,35,.05)', border:'1px solid rgba(245,166,35,.2)', borderRadius:8, padding:'12px 14px' }}>
                  <div style={{ fontSize:11, fontWeight:800, color:'var(--gold)', marginBottom:8 }}>
                    ⚠ {genResult.warnings.length} Warning{genResult.warnings.length!==1?'s':''}
                  </div>
                  <div style={{ maxHeight:150, overflowY:'auto' }}>
                    {genResult.warnings.map((w,i) => (
                      <div key={i} style={{ fontSize:11, padding:'3px 0', borderBottom:'1px solid rgba(245,166,35,.1)', color:'var(--gold)' }}>⚠ {w}</div>
                    ))}
                  </div>
                </div>
              )}
            </Section>
          </div>
        </div>
      )}
    </div>
  );
}
